import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  const location = useLocation();
  const lang = location.pathname.startsWith('/de') ? 'de' : 'en';
  
  const content = {
    en: {
      address: 'Address',
      contact: 'Contact',
      services: 'Services',
      legal: 'Legal',
      servicesList: ['Audio Podcasts', 'Video Podcasts', 'Webinars', 'Live Streams'],
      legalList: ['Privacy Policy', 'Terms of Service', 'Imprint'],
      rights: 'All rights reserved.',
    },
    de: {
      address: 'Adresse',
      contact: 'Kontakt',
      services: 'Dienstleistungen',
      legal: 'Rechtliches',
      servicesList: ['Audio-Podcasts', 'Video-Podcasts', 'Webinare', 'Live-Streams'],
      legalList: ['Datenschutz', 'AGB', 'Impressum'],
      rights: 'Alle Rechte vorbehalten.',
    },
  };

  const t = content[lang];

  return (
    <footer className="bg-gray-900 text-white" role="contentinfo" itemScope itemType="https://schema.org/WPFooter">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/t4qrt8st_Ein%20Logo%20fu%CC%88r%20die%20Marke%20Podcast%20Zurich%20%20Es%20muss%20urban%20ru%CC%88ber%20kommen-1.png"
              alt="Podcast Zurich Logo"
              className="h-16 w-auto mb-4"
              width="200"
              height="64"
            />
            <p className="text-gray-400 text-sm">
              {lang === 'en'
                ? 'Professional podcast production in the heart of Zurich'
                : 'Professionelle Podcast-Produktion im Herzen von Zürich'}
            </p>
          </div>

          {/* Address */}
          <div itemScope itemType="https://schema.org/PostalAddress">
            <h4 className="font-semibold mb-4">{t.address}</h4>
            <address className="space-y-2 text-gray-400 text-sm not-italic">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" aria-hidden="true" />
                <span>
                  <span itemProp="streetAddress">Werdstrasse 36</span><br />
                  <span itemProp="postalCode">8004</span> <span itemProp="addressLocality">Zürich</span><br />
                  <span itemProp="addressCountry">Schweiz</span>
                </span>
              </div>
            </address>
          </div>

          {/* Services */}
          <nav aria-label="Services">
            <h4 className="font-semibold mb-4">{t.services}</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              {t.servicesList.map((service, index) => (
                <li key={index}>{service}</li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4">{t.contact}</h4>
            <div className="space-y-2 text-gray-400 text-sm">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" aria-hidden="true" />
                <a href="tel:+41772179528" className="hover:text-white transition-colors" itemProp="telephone">
                  077 217 95 28
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" aria-hidden="true" />
                <a href="mailto:info@podcastzurich.com" className="hover:text-white transition-colors" itemProp="email">
                  info@podcastzurich.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © 2026 Podcast Zurich. {t.rights}
          </p>
          <div className="flex items-center gap-6">
            {/* Social Media Links */}
            <nav aria-label="Social media" className="flex gap-3">
              <a
                href="https://www.instagram.com/podcast_zurich/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow Podcast Zurich on Instagram"
              >
                <Instagram className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="https://www.linkedin.com/company/podcast-zurich/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow Podcast Zurich on LinkedIn"
              >
                <Linkedin className="w-5 h-5" aria-hidden="true" />
              </a>
            </nav>
            <Link to={`/${lang}/impressum`} className="text-sm text-gray-400 hover:text-white transition-colors">
              Impressum
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;